import React from 'react';

import { List } from 'antd';
import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import CommunitySettingLabel from 'components/Settings/CommunitySettingLabel';
import { CMI_DIGEST_FREQUENCY_OPTIONS, COMMUNITY_LABEL_COLOR } from 'components/Settings/constants';

import { CmiDigestAdminUser } from '../types';

const StyledListItem = styled(List.Item)`
  background-color: white;
  cursor: pointer;

  &&.ant-list-item {
    padding: 15px 10px 15px 15px;
  }

  &:hover {
    background-color: var(--background-gray-light);
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const NameEmail = styled.div`
  display: flex;
  flex-direction: column;
`;

const NameText = styled.span`
  font-family: var(--font-bold);
  font-size: 14px;
`;

const EmailText = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 20px;
`;

const StatusColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const StatusSubtext = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

type Props = {
  user: CmiDigestAdminUser;
  isMultipleSelection: boolean;
  isSelected: boolean;
  onToggleSelect: (user: CmiDigestAdminUser) => void;
  onClick: (user: CmiDigestAdminUser) => void;
};

const CmiDigestAdminListItem = ({ user, isMultipleSelection, isSelected, onToggleSelect, onClick }: Props) => {
  const { enabled, frequency, competitor_incentives_enabled, community_reviews_enabled } = user.cmi_digest_preference;

  const contentTypes = [
    competitor_incentives_enabled && 'Competitor Incentives',
    community_reviews_enabled && 'My Community Reviews',
  ].filter(Boolean);

  const frequencyLabel = CMI_DIGEST_FREQUENCY_OPTIONS.find((option) => option.value === frequency)?.label ?? frequency;

  return (
    <StyledListItem
      style={{ cursor: isMultipleSelection ? 'auto' : 'pointer' }}
      onClick={() => onClick(user)}
      data-test-id={`cmi-digest-admin-row-${user.id}`}
    >
      <LeftContainer>
        {isMultipleSelection && (
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(user)}
            style={{ marginRight: '15px' }}
            data-test-id={`cmi-digest-admin-row-select-${user.id}`}
          />
        )}
        <NameEmail>
          <NameText data-test-id={`cmi-digest-admin-row-name-${user.id}`}>{user.name}</NameText>
          <EmailText data-test-id={`cmi-digest-admin-row-email-${user.id}`}>{user.email}</EmailText>
        </NameEmail>
      </LeftContainer>
      <RightContainer>
        <StatusColumn>
          <CommunitySettingLabel
            color={enabled ? COMMUNITY_LABEL_COLOR.POSITIVE : COMMUNITY_LABEL_COLOR.NEUTRAL}
            dataTestId={`cmi-digest-admin-row-status-${user.id}`}
          >
            {enabled ? 'On' : 'Off'}
          </CommunitySettingLabel>
          {enabled && contentTypes.length > 0 && (
            <StatusSubtext data-test-id={`cmi-digest-admin-row-subtext-${user.id}`}>
              {frequencyLabel} · {contentTypes.join(', ')}
            </StatusSubtext>
          )}
        </StatusColumn>
        <img src="/icons/chevron-right.svg" style={{ width: '24px', height: '24px' }} />
      </RightContainer>
    </StyledListItem>
  );
};

export default CmiDigestAdminListItem;
